import React from "react";
import { Link } from "wouter";
import { SEOHead } from "@/components/SEOHead";
import { Header, Footer, Breadcrumbs } from "@/components/Navigation";
import { PRACTICE_INFO } from "@/site-data";
import {
  MapPin,
  Clock,
  Phone,
  Mail,
  Calendar,
  CheckCircle,
  Car,
  Navigation as NavigationIcon,
  ShieldCheck
} from "lucide-react";

export default function HeberOffice() {
  const officeSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    name: "Wasatch Foot & Ankle Center - Heber City Office",
    image: "https://www.drspenceharper.com/manus-storage/reception-main_cb5cd9eb.webp",
    url: "https://www.drspenceharper.com/heber-office/",
    telephone: PRACTICE_INFO.officePhone,
    alternateName: "Dr. Spence Harper Podiatry Office",
    address: {
      "@type": "PostalAddress",
      streetAddress: "190 N. Main Street",
      addressLocality: "Heber City",
      addressRegion: "UT",
      postalCode: "84032",
      addressCountry: "US"
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: 40.5097,
      longitude: -111.4133
    },
    openingHoursSpecification: [
      {
        "@type": "OpeningHoursSpecification",
        dayOfWeek: ["Monday", "Tuesday", "Thursday"],
        opens: "08:00",
        closes: "17:00"
      }
    ]
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEOHead
        title="Heber City Podiatry Office | Wasatch Foot & Ankle Center"
        description="Visit Wasatch Foot & Ankle Center at 190 N. Main Street in Heber City, UT. View office hours, phone numbers, parking info, and book an appointment with Dr. Spence Harper."
        canonicalPath="/heber-office/"
        schema={officeSchema}
      />
      <Header />
      <Breadcrumbs items={[{ label: "Heber Office" }]} />

      <main className="flex-1">
        {/* HERO HEADER */}
        <section className="bg-[#0f2b48] text-white py-14">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-3">
            <span className="text-cyan-400 font-bold text-xs uppercase tracking-widest">
              Wasatch County Foot &amp; Ankle Clinic
            </span>
            <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight">
              Our Heber City Office
            </h1>
            <p className="text-slate-300 text-base max-w-2xl mx-auto font-body">
              Conveniently located on North Main Street in Heber City, serving patients throughout Heber Valley, Midway, Charleston, and Park City.
            </p>
          </div>
        </section>

        {/* OFFICE DETAILS SECTION */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
              {/* Left Column: Office Specs */}
              <div className="lg:col-span-6 space-y-8">
                <div>
                  <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0f2b48] mb-3">
                    Clinic Location &amp; Hours
                  </h2>
                  <p className="text-slate-600 text-base font-body leading-relaxed">
                    Our modern Heber City office is outfitted for state-of-the-art diagnostic evaluations, in-office minor surgical procedures, and breakthrough Swift microwave wart treatments.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 space-y-3">
                    <div className="flex items-center gap-2 text-[#0284c7] font-bold text-sm uppercase tracking-wider">
                      <MapPin className="w-4 h-4" /> Address
                    </div>
                    <div className="text-sm text-slate-800 font-medium">
                      Wasatch Foot &amp; Ankle Center<br />
                      190 N. Main Street<br />
                      Heber City, UT 84032
                    </div>
                    <div className="pt-2">
                      <a
                        href="https://goo.gl/maps/ewyayURMNstjitQg9"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-bold text-[#0284c7] hover:underline inline-flex items-center gap-1"
                      >
                        <NavigationIcon className="w-3.5 h-3.5" /> Open in Google Maps
                      </a>
                    </div>
                  </div>

                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 space-y-3">
                    <div className="flex items-center gap-2 text-[#0284c7] font-bold text-sm uppercase tracking-wider">
                      <Phone className="w-4 h-4" /> Phone Numbers
                    </div>
                    <div className="text-sm text-slate-800 space-y-1">
                      <div>
                        Office Phone:{" "}
                        <a href={`tel:${PRACTICE_INFO.officePhone}`} className="font-bold text-[#0f2b48] hover:text-[#0284c7]">
                          (435) 657-0329
                        </a>
                      </div>
                      <div className="text-xs text-slate-500 pt-1">
                        Fax: {PRACTICE_INFO.fax}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Office Hours Table */}
                <div className="bg-slate-50 border border-slate-200 rounded-2xl p-6 space-y-4">
                  <div className="flex items-center gap-2 text-[#0f2b48] font-heading font-bold text-lg">
                    <Clock className="w-5 h-5 text-[#0284c7]" /> Weekly Schedule
                  </div>
                  <div className="divide-y divide-slate-200 text-sm">
                    {PRACTICE_INFO.hours.map((h, idx) => (
                      <div key={idx} className="py-2.5 flex justify-between items-center text-slate-700">
                        <span className="font-medium">{h.days}</span>
                        <span className={h.hours.includes("Closed") ? "text-amber-700 font-semibold text-xs" : "font-bold text-slate-900"}>
                          {h.hours}
                        </span>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 italic">
                    *Wednesdays are reserved exclusively for outpatient foot &amp; ankle surgical procedures at accredited surgical centers.
                  </p>
                </div>

                {/* Parking & Accessibility */}
                <div className="space-y-3">
                  <h3 className="font-heading font-bold text-lg text-[#0f2b48]">
                    Parking &amp; Facility Accessibility
                  </h3>
                  <div className="space-y-2 text-sm text-slate-600">
                    <div className="flex items-start gap-2.5">
                      <Car className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span>Free ground-level parking directly adjacent to the clinic entrance.</span>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <CheckCircle className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span>Handicap-accessible ramps and wide entryways for wheelchairs and knee scooters.</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Reception Photo & Embedded Interactive Map */}
              <div className="lg:col-span-6 space-y-6">
                <div className="rounded-2xl overflow-hidden shadow-md border border-slate-200">
                  <img
                    src={PRACTICE_INFO.receptionPhoto}
                    alt="Wasatch Foot and Ankle Center Front Reception Desk"
                    loading="lazy"
                    className="w-full h-72 sm:h-80 object-cover"
                  />
                  <div className="p-4 bg-white text-xs text-slate-600 flex items-center justify-between">
                    <span className="font-bold text-[#0f2b48]">Wasatch Foot &amp; Ankle Reception</span>
                    <span>190 N. Main St, Heber City</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <figure className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm">
                    <img src={PRACTICE_INFO.receptionSecondaryPhoto} alt="Wasatch Foot & Ankle Center waiting area" loading="lazy" className="h-56 w-full object-cover" />
                    <figcaption className="px-3 py-2 text-xs font-semibold text-[#0f2b48]">Patient waiting area</figcaption>
                  </figure>
                  <figure className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm">
                    <img src={PRACTICE_INFO.treatmentRoomPhoto} alt="Wasatch Foot & Ankle Center treatment room" loading="lazy" className="h-56 w-full object-cover" />
                    <figcaption className="px-3 py-2 text-xs font-semibold text-[#0f2b48]">Treatment room</figcaption>
                  </figure>
                </div>

                {/* Google Maps Embed */}
                <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm h-80 sm:h-96">
                  <iframe
                    title="Wasatch Foot & Ankle Center Heber City Location"
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3032.551694294406!2d-111.41544862343276!3d40.51010397142478!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x874df3b3636ff059%3A0x5140beeee6c8c6d5!2s190%20N%20Main%20St%2C%20Heber%20City%2C%20UT%2084032!5e0!3m2!1sen!2sus!4v1700000000000!5m2!1sen!2sus"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen={false}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                  ></iframe>
                </div>

                <div className="p-6 bg-slate-50 rounded-2xl border border-slate-200 text-center space-y-3">
                  <h4 className="font-heading font-bold text-base text-[#0f2b48]">
                    Need Directions or Have Questions?
                  </h4>
                  <p className="text-xs text-slate-600">
                    Our team is here to help you get scheduled quickly and smoothly.
                  </p>
                  <Link
                    href="/appointments/"
                    className="inline-flex items-center gap-2 bg-[#0284c7] hover:bg-[#0369a1] text-white font-bold text-xs px-6 py-3 rounded-lg shadow transition-all"
                  >
                    <Calendar className="w-4 h-4" /> Request Appointment Online
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
